import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ChevronsLeftRight } from "lucide-react";

interface ShoePair {
  before: string;
  after: string;
}

interface BeforeAfterSliderProps {
  beforeSrc?: string; // left image (e.g., dirty) - deprecated, use pairs instead
  afterSrc?: string; // right image (e.g., clean) - deprecated, use pairs instead
  pairs?: ShoePair[]; // array of before/after pairs for auto-switching
  alt?: string;
  initialPosition?: number; // 0-100
  className?: string;
  circleClassName?: string;
  autoAnimate?: boolean; // enable auto left-to-right animation
  animationDuration?: number; // duration of one sweep in ms (default 3000)
  autoSwitch?: boolean; // enable auto-switching between pairs
  switchInterval?: number; // interval between pair switches in ms (default 5000)
  onPairChange?: (index: number) => void; // callback when pair changes
  currentPairIndex?: number; // controlled pair index from parent
}

const clamp = (val: number, min: number, max: number) =>
  Math.max(min, Math.min(max, val));

const BeforeAfterSlider: React.FC<BeforeAfterSliderProps> = ({
  beforeSrc,
  afterSrc,
  pairs = [],
  alt = "Before and after comparison",
  initialPosition = 50,
  className = "",
  circleClassName = "",
  autoAnimate = false,
  animationDuration = 3000,
  autoSwitch = false,
  switchInterval = 5000,
  onPairChange,
  currentPairIndex: controlledPairIndex,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState(() => clamp(initialPosition, 0, 100));
  const [dragging, setDragging] = useState(false);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const [internalPairIndex, setInternalPairIndex] = useState(0);
  // animationRef removed - using local rafId in useEffect instead
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use controlled index if provided, otherwise use internal state
  const currentPairIndex =
    controlledPairIndex !== undefined ? controlledPairIndex : internalPairIndex;

  // Determine current images (support both single pair and multiple pairs)
  const allPairs: ShoePair[] = useMemo(() => {
    if (pairs.length > 0) return pairs;
    if (beforeSrc && afterSrc) return [{ before: beforeSrc, after: afterSrc }];
    return [];
  }, [pairs, beforeSrc, afterSrc]);

  const pairsCount = allPairs.length;
  const currentPair = allPairs[currentPairIndex] || { before: "", after: "" };

  // Only run the auto-sweep while the slider is on screen (saves a 60fps
  // re-render loop for the whole hero once the visitor scrolls past it).
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-animate slider position (left to right, then reset)
  useEffect(() => {
    if (!autoAnimate || !visible || dragging || isUserInteracting || pairsCount === 0) {
      return;
    }

    let startTime: number | null = null;
    let rafId: number;

    const animate = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = (elapsed % animationDuration) / animationDuration;

      // Ease in-out for smooth animation
      const easeInOut = (t: number) =>
        t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

      // Ping-pong: go from 15% to 85% and back
      const cycle = Math.floor(elapsed / animationDuration) % 2;
      const easedProgress = easeInOut(progress);
      const newPos =
        cycle === 0
          ? 15 + easedProgress * 70 // 15 to 85
          : 85 - easedProgress * 70; // 85 to 15

      setPos(newPos);
      rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [autoAnimate, visible, dragging, isUserInteracting, animationDuration, pairsCount]);

  // Auto-switch between pairs (only if not controlled from parent)
  useEffect(() => {
    if (
      !autoSwitch ||
      pairsCount <= 1 ||
      isUserInteracting ||
      controlledPairIndex !== undefined
    )
      return;

    const interval = setInterval(() => {
      setInternalPairIndex((prev) => {
        const next = (prev + 1) % pairsCount;
        onPairChange?.(next);
        return next;
      });
    }, switchInterval);

    return () => clearInterval(interval);
  }, [
    autoSwitch,
    pairsCount,
    switchInterval,
    isUserInteracting,
    onPairChange,
    controlledPairIndex,
  ]);

  // Handle user interaction - pause animations temporarily
  const handleUserInteraction = useCallback(() => {
    setIsUserInteracting(true);

    // Clear existing timeout
    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    // Resume auto-animation after 5 seconds of no interaction
    interactionTimeoutRef.current = setTimeout(() => {
      setIsUserInteracting(false);
    }, 5000);
  }, []);

  const updateFromClientX = useCallback(
    (clientX: number) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const percent = ((clientX - rect.left) / rect.width) * 100;
      setPos(clamp(percent, 0, 100));
      handleUserInteraction();
    },
    [handleUserInteraction]
  );

  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    handleUserInteraction();
    updateFromClientX(e.clientX);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setDragging(true);
    handleUserInteraction();
    updateFromClientX(e.touches[0].clientX);
  };

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e: MouseEvent) => updateFromClientX(e.clientX);
    const onUp = () => setDragging(false);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, updateFromClientX]);

  useEffect(() => {
    if (!dragging) return;
    const onTouchMove = (e: TouchEvent) =>
      updateFromClientX(e.touches[0].clientX);
    const onTouchEnd = () => setDragging(false);

    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [dragging, updateFromClientX]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={"relative select-none touch-none " + className}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      role="slider"
      aria-label={alt}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") setPos((p) => clamp(p - 2, 0, 100));
        if (e.key === "ArrowRight") setPos((p) => clamp(p + 2, 0, 100));
      }}
    >
      {/* Bottom (after/clean) full-size, static */}
      <img
        src={currentPair.after}
        alt={alt}
        width={1400}
        height={933}
        className="block w-full h-auto object-cover transition-opacity duration-500"
      />

      {/* Top (before/dirty) full-size, clipped via clip-path so it doesn't slide */}
      <img
        src={currentPair.before}
        alt=""
        aria-hidden
        width={1400}
        height={933}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-500"
        style={{
          clipPath: `inset(0 ${100 - pos}% 0 0)`,
        }}
      />

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white/70"
        style={{ left: `calc(${pos}% - 0.5px)` }}
      />

      {/* Handle */}
      <div
        className={
          "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-white shadow-md border border-black/5 flex items-center justify-center cursor-grab " +
          circleClassName
        }
        style={{ left: `${pos}%` }}
      >
        <div className="flex items-center text-gray-500">
          <ChevronsLeftRight className="w-6 h-6" />
        </div>
      </div>

      {/* Accessible range input visually hidden but focusable for keyboard control */}
      <input
        type="range"
        min={0}
        max={100}
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        className="absolute inset-0 opacity-0 cursor-col-resize"
        aria-label={alt}
      />
    </div>
  );
};

export default BeforeAfterSlider;
